<script setup>
import { ref, onMounted, onUnmounted, watch } from 'vue'

const props = defineProps({
  map: {
    type: Object,
    required: true,
  },
  id: {
    type: String,
    required: true,
  },
  type: {
    type: String,
    required: true,
    validator: (value) =>
      ['geojson', 'vector', 'raster', 'image', 'video'].includes(value),
  },
  data: {
    type: Object,
    required: false,
    default: () => ({}),
  },
  options: {
    type: Object,
    required: false,
    default: () => ({}),
  },
  url: {
    type: String,
    required: false,
    default: '',
  },
  coordinates: {
    type: Array,
    required: false,
    default: () => [],
  },
})

const isAdded = ref(false)

const addSource = () => {
  if (props.map.getSource(props.id)) return

  const allowedKeys = [
    'type',
    'data', 'maxzoom', 'attribution', 'buffer', 'tolerance', 'cluster', 'clusterRadius',
    'clusterMaxZoom', 'clusterMinPoints', 'clusterProperties', 'lineMetrics', 'generateId', 'promoteId',
    'url', 'tiles', 'minzoom', 'scheme',
    'tileSize', 'bounds', 'encoding'
  ]

  const source = { type: props.type }
  Object.entries(props.options || {}).forEach(([key, val]) => {
    if (allowedKeys.includes(key)) source[key] = val
  })

  if (props.type === 'geojson') {
    source.data = props.data
    source.promoteId = 'code'
  }

  if (props.type === 'image') {
    source.url = props.url
    source.coordinates = props.coordinates
  } else if (props.type === 'raster' && props.url) {
    source.url = props.url
  }

  try {
    props.map.addSource(props.id, source)
    isAdded.value = true
  } catch (err) {
    // Style still processing (e.g. inside mapbox load event) — retry on next styledata tick
    props.map.once('styledata', addSource)
  }
}

const removeSource = () => {
  // During tile teardown the parent MapCanvas may have already removed the
  // instance's style; getSource() then dereferences an undefined style and throws.
  if (!props.map?.style) return
  try {
    if (props.map.getSource(props.id)) {
      props.map.removeSource(props.id)
    }
  } catch (_) { /* instance being disposed */ }
}

onMounted(() => {
  addSource()
  props.map.on('style.load', addSource)
})

onUnmounted(() => {
  removeSource()
  props.map.off('style.load', addSource)
  props.map.off('styledata', addSource)
})

// Not deep: callers pass freshly-built collections, so identity already changes.
watch(() => props.data, (newValue) => {
  if (!isAdded.value || !newValue) return
  const src = props.map.getSource(props.id)
  if (src && typeof src.setData === 'function') {
    src.setData(newValue)
  }
})

</script>

<template>
  <slot v-if="isAdded"></slot>
</template>

